"use client"

import { motion } from "framer-motion"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Shield, Eye, Lock, Users, FileText, Phone } from "lucide-react"
import TextReveal from "@/components/animated/text-reveal"
import FloatingCard from "@/components/animated/floating-card"
import GradientBackground from "@/components/animated/gradient-background"

export default function PrivacyClientPage() {
  const sections = [
    {
      icon: Eye,
      title: "Information We Collect",
      content: [
        "Personal identification information (name, email, phone number, address)",
        "Business information (company name, industry, project details)",
        "Website usage data (pages visited, time spent, browser information)",
        "Communication records (emails, phone calls, chat messages)",
        "Payment information (processed securely through third-party providers)",
      ],
    },
    {
      icon: Users,
      title: "How We Use Your Information",
      content: [
        "Process and fulfill your hardware orders and quotes",
        "Communicate about products, services, and business updates",
        "Improve our website functionality and user experience",
        "Provide customer support and technical assistance",
        "Comply with legal obligations and business requirements",
      ],
    },
    {
      icon: Lock,
      title: "Data Protection & Security",
      content: [
        "We use industry-standard encryption to protect your data",
        "Access to personal information is restricted to authorized personnel",
        "Regular security audits and updates to our systems",
        "Secure data storage with backup and recovery procedures",
        "Compliance with applicable data protection regulations",
      ],
    },
    {
      icon: Shield,
      title: "Your Rights",
      content: [
        "Access and review your personal information",
        "Request corrections to inaccurate data",
        "Request deletion of your personal information",
        "Opt-out of marketing communications",
        "Data portability and transfer rights",
      ],
    },
  ]

  return (
    <div className="min-h-screen relative">
      <GradientBackground />

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-cyan-600/20" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="bg-white/10 text-white border-white/20 mb-6 backdrop-blur-sm">
                <Shield className="h-4 w-4 mr-2" />
                Privacy Policy
              </Badge>
            </motion.div>
            <TextReveal>
              <h1 className="text-4xl lg:text-6xl font-bold mb-6">Your Privacy Matters</h1>
            </TextReveal>
            <TextReveal delay={0.2}>
              <p className="text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
                At Real Metro Metal, we are committed to protecting your privacy and personal information. This policy
                explains how we collect, use, and safeguard your data.
              </p>
            </TextReveal>
            <TextReveal delay={0.4}>
              <p className="text-sm text-slate-400 mt-4">
                Last updated:{" "}
                {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
              </p>
            </TextReveal>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-20 relative z-10">
        {/* Main Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {sections.map((section, index) => (
            <FloatingCard key={section.title} delay={index * 0.1}>
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-500 h-full">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white p-3 rounded-lg">
                      <section.icon className="h-6 w-6" />
                    </div>
                    <span className="text-xl">{section.title}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-slate-400 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-slate-600 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </FloatingCard>
          ))}
        </div>

        {/* Additional Information */}
        <div className="space-y-8">
          <FloatingCard delay={0.4}>
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <FileText className="h-6 w-6 text-slate-700" />
                  <span>Cookies and Tracking</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-600 leading-relaxed">
                  We use cookies and similar technologies to enhance your browsing experience, analyze website traffic,
                  and understand user preferences. You can control cookie settings through your browser preferences.
                </p>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Types of cookies we use:</h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• Essential cookies for website functionality</li>
                    <li>• Analytics cookies to understand user behavior</li>
                    <li>• Preference cookies to remember your settings</li>
                    <li>• Marketing cookies for relevant advertisements</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </FloatingCard>

          <FloatingCard delay={0.5}>
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <Phone className="h-6 w-6 text-slate-700" />
                  <span>Contact Us About Privacy</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed mb-4">
                  If you have questions about this privacy policy or how we handle your personal information, please
                  contact us:
                </p>
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    <strong>Email:</strong> privacy@realmetrometal.com
                  </p>
                  <p className="text-sm">
                    <strong>Phone:</strong> +94 77 393 3643
                  </p>
                  <p className="text-sm">
                    <strong>Address:</strong> 392 1/3 Old Moor St, Colombo 01200, Sri Lanka
                  </p>
                </div>
              </CardContent>
            </Card>
          </FloatingCard>
        </div>
      </div>
    </div>
  )
}
